import {
  ACTION_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  INPUT_FORM_ARGS,
  INPUT_MENU_ARGS,
  Metadata,
  pick,
} from '../../../../utils/spds/metadata';
import { ensureSafeComponent } from '@embroider/util';
import { fn, hash } from '@ember/helper';
import FlexColumn from '../../layout/flex/column.gjs';
import HelperText from '../../core/helper-text.gjs';
import Label from './list/label.gjs';
import ListMenu from './list/menu.gjs';
import ListMultiSelectMenu from './list/multiselect-menu.gjs';
import Super from '../menu-parent.gjs';
import Trigger from './list/trigger.gjs';
import closeOnClickOutside from '../../../../modifiers/close-on-click-outside';

const LABEL_SELECTOR = 'spds-input-dropdown-list-label';

export const GAP_DEFAULT = '4px';

const METADATA = Metadata.define({
  description: `Dropdowns allow users to select a single item or multiple items from a list of options in a limited space. The list of options can change based on the context.

### Contextual components
The label, trigger, menu, helper text, and menu item are passed into the dropdown as yielded components using the \`label\`, \`trigger\`, \`menu\`, \`helperText\`, and \`item\` keys. For the multiselect variant, the menu component also has its own \`item\` yielded.

### Basic Dropdown
The basic dropdown creates a list of buttons that trigger the passed in \`@onChange\` action when clicked. Only one option can be selected at a time, and it is possible to deselect an option by clicking it when it is already selected. The \`@onChange\` action receives the click action from the button that was clicked.

Instances of the \`item\` component can be provided a \`@value\` argument that is assigned to the \`data-value\` attribute of the button. This can be accessed in the event object returned to \`@onChange\` through \`event.target.dataset.value\`. Alternatively, the dropdown can also use the text of the option to properly select the correct item. A dropdown can be instantiated with a preselected value using either of these values when the \`@value\` argument is passed to the \`Spds:Input::Dropdown::List\` component.

### Multiselect Dropdown
The multiselect variant of the dropdown uses the \`Spds::Input:Group::Checkbox\` to drive its list of options. Each \`item\` is a \`Spds::Input::Checkbox\` component that is automatically managed by the containing group component. Their state can be managed by passing \`@checked\`, \`@label\`, and \`@value\` attributes directly to each item.

A specialized checkbox for a \`Select all\` option will appear automatically if there are six or more options in the list. If customized label text is desired for this checkbox, there is a \`@selectAllLabel\` attribute that can be passed to the menu component.

Checkboxes trigger the passed in \`@onChange\` action when clicked. The action will receive the same data as the \`Spds::Input::Group::Checkbox\`, which is an object containing three values:

* \`event\` - the click event object
* \`groupName\` - the group name of the checkbox group
* \`values\` - an array of the values for the checked checkboxes

If a multiselect dropdown is initialized with values selected, it will emit a modified \`onChange\` action on insert that does not include the \`event\` object.`,
  extends: [
    pick(ACTION_ARGS, ['onClose', 'onOpen']),
    pick(COMPONENT_STATE_ARGS, ['disabled', 'error']),
    pick(INPUT_FORM_ARGS, [
      { name: 'helperText', initialValue: 'Helper text' },
      { name: 'label', initialValue: 'Label' },
      { name: 'placeholder', initialValue: 'Placeholder' },
      { name: 'required', initialValue: true },
      'value',
    ]),
    pick(INPUT_MENU_ARGS, ['multiselect']),
  ],
  meta: {
    onChange: Arg.action({ description: 'Called when selection changes' }).required(),
    selectedValues: Arg.array({
      description: 'Array of values for externally controlling multiselect checkbox state',
    }),
  },
  name: 'Dropdown List',
});

export default class SpdsInputDropdownList extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  baseClass = 'spds-input-dropdown-list';
  multiselectMenuComponent = ListMultiSelectMenu;
  registeredItems = [];
  setupRun = false;
  singleSelectMenuComponent = ListMenu;

  get gap() {
    return this.args.gap ?? GAP_DEFAULT;
  }

  <template>
    <FlexColumn
      @gap={{this.gap}}
      @inputContainer={{true}}
      class={{this.classes}}
      data-test-spds-input-dropdown-list
      id={{this.id}}
      ...attributes
      {{closeOnClickOutside this.handleClose LABEL_SELECTOR}}
      {{this.onInsert}}
      {{this.setupComponent}}
      {{this.syncExternalSelection}}
      {{this.updateValueFromExternalSource}}
      {{this.validateArguments}}
    >
      {{yield
        (hash
          helperText=(component HelperText helperText=@helperText id=this.helperTextId error=@error)
          label=(component Label id=this.labelId label=@label required=@required)
          menu=(component
            (ensureSafeComponent this.menuType)
            anchorElement=this.triggerContainer
            displayedItemCount=this.options.length
            open=this.open
            id=this.menuId
            onClick=this.onChange
            onSelect=this.onSelect
            registerComponent=(fn this.registerComponent "menuComponent")
            retainSelection=this.retainSelection
            unregisterComponent=(fn this.unregisterComponent "menuComponent")
          )
          trigger=(component
            Trigger
            clearSelection=this.clearSelection
            disabled=@disabled
            error=@error
            open=this.open
            menuId=this.menuId
            multiselect=@multiselect
            onClick=this.toggle
            onClose=this.handleClose
            placeholder=@placeholder
            registerComponent=(fn this.registerComponent "triggerComponent")
            selected=this.selected
            triggerId=this.triggerId
            unregisterComponent=(fn this.unregisterComponent "triggerComponent")
          )
        )
      }}
    </FlexColumn>
  </template>
}

export const INPUT_DROPDOWN_LIST_METADATA = SpdsInputDropdownList.metadata;
